import { Text, View } from '@react-pdf/renderer';
import logo from '@ui/assets/images/WWP/WESTERN_WIRE_RGB_PRIMARY_LOGO_AW.png';
import { EnvConfig } from '@ui/config/env';

import { usePDFContext } from '../PDFProvider';
import { EmployeeInformation } from './components/EmployeeInformation';
import { Header } from './components/Header';
import { Logo } from './components/Logo';
import { Page } from './components/Page';
import { CustomerAddress } from './components/QuoteDetails/CustomerAddress';
import { CustomerContact } from './components/QuoteDetails/CustomerContact';
import { QuoteDetails } from './components/QuoteDetails/QuoteDetails';
import { QuoteInformation } from './components/QuoteDetails/QuoteInformation';
import { QuoteId } from './components/QuoteId';
import { QuoteLineForPart, QuoteLineForServices } from './components/QuoteLine';
import { PartDescription } from './components/QuoteLine/PartDescription';
import { PartId } from './components/QuoteLine/PartId';
import { PartSpecs } from './components/QuoteLine/PartSpecs';
import { SiteInformation } from './components/SiteInformation';
import { Specs } from './components/Specs';
import { styles } from './components/styles';
import { TermsAndConditions } from './components/TermsAndConditions';

const WWP_SITE_INFORMATION = [
  '770 Sunpark Drive',
  'Fenton, MO 63026',
  'Phone (800) 325 - 3770',
  'sales@westernwireusa.com',
];

export function WWPQuoteDocument() {
  const { customer, quoteLines } = usePDFContext();

  return (
    <Page>
      <Header>
        <View style={styles.column}>
          <View style={styles.row}>
            <Logo
              source={logo}
              text="ISO 9001:2015 certified"
              extraStyles={[{ height: '42px' }]}
            />

            <SiteInformation name="Western Wire" items={WWP_SITE_INFORMATION} />

            <QuoteId ecommerceUri={EnvConfig.getKey('MW_COMPONENTS_URI')} />
          </View>
          <View style={styles.row}>
            <View style={styles.column}>
              <View style={styles.row}>
                <View style={[styles.sectionHalf, { marginBottom: 4 }]}>
                  <View style={styles.row}>
                    <Text style={[styles.bold, styles.textReverse]}>
                      Minimum Purchase:
                    </Text>
                  </View>
                  <Text style={styles.textSmall}>
                    $125.00 combined product total, per schedule release.
                  </Text>
                  <Text style={styles.textSmall}>
                    Fees do not apply towards minimum.
                  </Text>
                </View>

                <View style={[styles.sectionHalf, { marginBottom: 4 }]}>
                  <View style={styles.row}>
                    <Text style={[styles.bold, styles.textReverse]}>
                      Made-to-Order Items:
                    </Text>
                  </View>
                  <Text style={styles.textSmall}>
                    Delivery quantity variance of +/- 5-10% may apply.
                  </Text>
                </View>
              </View>

              <View style={styles.row}>
                <View style={[styles.fullRow, { marginBottom: 4 }]}>
                  <View style={styles.row}>
                    <Text style={[styles.bold, styles.textReverse]}>
                      Certifications:
                    </Text>
                  </View>

                  <Text style={styles.textSmall}>
                    Certifications are optional. Fees are per item—unless
                    specified otherwise—and apply to each requested release.
                  </Text>
                </View>
              </View>

              <View style={styles.row}>
                <View
                  style={[styles.fullRow, { marginBottom: 4, width: '100%' }]}
                >
                  <View
                    style={[
                      styles.row,
                      {
                        marginBottom: 4,
                        gap: 8,
                      },
                    ]}
                  >
                    <Text
                      style={[styles.bold, styles.textSmall, { width: '15%' }]}
                    >
                      Non-Spring Pins:
                    </Text>
                    <Text style={[styles.textSmall, { width: '16%' }]}>
                      CofC: $40.00 per order
                    </Text>
                    <Text style={[styles.textSmall, { width: '11%' }]}>
                      Material: $35.00
                    </Text>
                    <Text style={[styles.textSmall, { width: '11%' }]}>
                      Plating: $60.00
                    </Text>
                    <Text style={[styles.textSmall, { width: '32%' }]}>
                      DFARS: $85.00 (includes CofC + Material Cert.)
                    </Text>
                  </View>

                  <View style={[styles.row, { gap: 8 }]}>
                    <Text
                      style={[styles.bold, styles.textSmall, { width: '15%' }]}
                    >
                      Slotted Spring Pins:
                    </Text>
                    <Text style={[styles.textSmall, { width: '16%' }]}>
                      CofC: $60
                    </Text>
                    <Text style={[styles.textSmall, { width: '11%' }]}>
                      Material: $55.00
                    </Text>
                    <Text style={[styles.textSmall, { width: '11%' }]}>
                      Plating: $100.00
                    </Text>
                    <Text style={[styles.textSmall, { width: '15%' }]}>
                      DFARS: Not offered
                    </Text>
                    <Text style={[styles.textSmall, { width: '12%' }]}>
                      Dual Cert: $75.00
                    </Text>
                  </View>
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.fullRow}>
                  <Text style={styles.textSmall}>
                    For PPAP, ISIR, IMDS, FAIR (AS9102), or any other
                    certification, please request quotation.
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>
      </Header>

      <QuoteDetails>
        <CustomerAddress nameStyle={[styles.bold]}>
          {customer.id ? (
            <Text style={[styles.bold, { marginTop: 8 }]}>{customer.id}</Text>
          ) : null}
        </CustomerAddress>

        <CustomerContact />

        <QuoteInformation />
      </QuoteDetails>

      {quoteLines.map((line) => {
        const { part, ...lineProps } = line;
        if (!part) {
          return <QuoteLineForServices {...line} showDescription />;
        }
        return (
          <QuoteLineForPart
            key={line.id}
            {...lineProps}
            part={part}
            showDrawing
          >
            <PartId part={part} showCustomerPartId />

            <PartDescription description={part.description} />
            <PartSpecs specs={line.specs} />
          </QuoteLineForPart>
        );
      })}

      <Specs />

      <EmployeeInformation />

      <View style={styles.row}>
        <View style={styles.fullRow}>
          <Text>FOB: Origin</Text>
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.fullRow}>
          <Text style={[styles.textSmall, styles.bold]}>
            Items ordered as part of a blanket or multiple release agreement
            must be taken with 6 months manufacture. Quoted delivery subject to
            material availability at time of order . DFAR items subject to
            material availability at time of order.
          </Text>
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.fullRow}>
          <Text>View Your Order Status & Tracking Online:</Text>
          <Text>
            For updates on your order status and tracking information, log in to
            your account on MWComponents.com using the same email address
            associated with your order. Navigate to My Account, then select
            Orders. If you do not already have an account, you can create one.
            Orders associated with the account email address will automatically
            populate.
          </Text>
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.fullRow}>
          <TermsAndConditions />
        </View>
      </View>
    </Page>
  );
}
